/**
 * Role-based patient list/detail scope. See docs/PLAN_PATIENT_SCOPE_AND_ACCESS.md.
 * Phase 1: client-side only; Phase 2 will enforce in Firestore rules.
 */

import { getRolePermissions } from '../constants/rolePermissions';

export const SCOPE_MY = 'my';
export const SCOPE_TEAM = 'team';
export const SCOPE_ALL = 'all';

const MAX_CARE_MANAGERS = 4;
/** careManagerIds is the union of every program's care team (RPM team first). */
const MAX_CARE_TEAM_UNION = 16;

/** Roles eligible for patient care-team assignment slots (careManagerIds). */
export const ASSIGNABLE_CARE_TEAM_ROLES = new Set(['care manager', 'supervisor', 'admin', 'clinical admin']);

/**
 * True if a care app user may be assigned to a patient care-team slot.
 * @param {{ role?: string } | null | undefined} user
 */
export function isAssignableCareTeamMember(user) {
  const role = String(user?.role || '').toLowerCase().trim();
  return ASSIGNABLE_CARE_TEAM_ROLES.has(role);
}

/**
 * Assigned care manager UIDs for a patient (legacy single field + future careManagerIds).
 * @param {object|null|undefined} patient
 * @returns {string[]}
 */
export function getPatientCareManagerIds(patient) {
  if (!patient) return [];
  const raw = patient.careManagerIds;
  if (Array.isArray(raw)) {
    const out = [...new Set(raw.map((x) => String(x || '').trim()).filter(Boolean))];
    return out.slice(0, MAX_CARE_TEAM_UNION);
  }
  const legacy = [patient.careManagerId, patient.careManager]
    .map((x) => String(x || '').trim())
    .filter(Boolean);
  return [...new Set(legacy)];
}

/**
 * RPM care team (max 4). RPM pages, the assignment editors, and RPM reports use
 * this; patients written before rpmCareManagerIds existed fall back to careManagerIds.
 * @param {object|null|undefined} patient
 * @returns {string[]}
 */
export function getPatientRpmCareManagerIds(patient) {
  if (!patient) return [];
  if (Array.isArray(patient.rpmCareManagerIds)) {
    return [...new Set(patient.rpmCareManagerIds.map((x) => String(x || '').trim()).filter(Boolean))]
      .slice(0, MAX_CARE_MANAGERS);
  }
  return getPatientCareManagerIds(patient).slice(0, MAX_CARE_MANAGERS);
}

/**
 * True if patient has at least one assigned care manager (excluded from supervisor "team" list filters).
 */
export function patientHasAssignedCareManager(patient) {
  return getPatientCareManagerIds(patient).length > 0;
}

/**
 * Care manager UIDs supervised by this supervisor (same supervisorId on user doc).
 * @param {string} supervisorUid
 * @param {Array<{ id: string, role?: string, supervisorId?: string }>} users
 * @returns {Set<string>}
 */
export function getSupervisedCareManagerUidSet(supervisorUid, users) {
  const set = new Set();
  if (!supervisorUid || !Array.isArray(users)) return set;
  const sup = String(supervisorUid).trim();
  for (const u of users) {
    const role = String(u?.role || '').toLowerCase().trim();
    if (role !== 'care manager') continue;
    if (String(u?.supervisorId || '').trim() === sup) {
      set.add(u.id);
    }
  }
  return set;
}

/**
 * Default scope when opening Dashboard / Patients (same for both pages).
 * @param {string|undefined} role
 */
export function getDefaultScopeForRole(role) {
  const r = String(role || '').toLowerCase().trim();
  if (r === 'admin' || r === 'superuser' || r === 'clinical admin' || r === 'viewer') return SCOPE_ALL;
  if (r === 'supervisor') return SCOPE_TEAM;
  return SCOPE_MY;
}

/**
 * Which scope UI to render.
 * @returns {'care_manager_single' | 'supervisor_two' | 'admin_two' | 'hidden'}
 */
export function getScopeControlKind(role) {
  const r = String(role || '').toLowerCase().trim();
  if (r === 'care manager') return 'care_manager_single';
  if (r === 'supervisor') return 'supervisor_two';
  if (r === 'admin' || r === 'superuser' || r === 'clinical admin') return 'admin_two';
  // viewer: scope control hidden — their list is already filtered by client/provider access.
  return 'hidden';
}

/**
 * Scope options for the compact filter chip, by role. Returns null when the
 * scope control should be hidden entirely.
 * @param {string|undefined} role
 * @returns {{ value: string, label: string }[] | null}
 */
export function getScopeFilterOptions(role) {
  const kind = getScopeControlKind(role);
  if (kind === 'care_manager_single') {
    return [{ value: SCOPE_MY, label: 'My Patients' }];
  }
  if (kind === 'supervisor_two') {
    return [
      { value: SCOPE_MY, label: 'My Patients' },
      { value: SCOPE_TEAM, label: 'My Team' },
    ];
  }
  if (kind === 'admin_two') {
    return [
      { value: SCOPE_MY, label: 'My Patients' },
      { value: SCOPE_ALL, label: 'All Patients' },
    ];
  }
  return null;
}

/**
 * True if `scope` is a valid choice for the given role (used when restoring a
 * persisted scope so we never show e.g. "All Patients" to a care manager).
 * @param {string} scope
 * @param {string|undefined} role
 */
export function isScopeValidForRole(scope, role) {
  const opts = getScopeFilterOptions(role);
  if (!opts) return false;
  return opts.some((o) => o.value === scope);
}

/**
 * Matches Firestore patient create rules (admin, supervisor, clinical admin, or superuser with impersonation).
 * @param {{ role?: string, impersonatingTenantId?: string } | null | undefined} profile
 * @returns {boolean}
 */
export function canCreatePatients(profile) {
  const perms = getRolePermissions(profile?.role);
  if (!perms.canCreatePatients) return false;
  const r = String(profile?.role || '').toLowerCase().trim();
  if (r === 'superuser') {
    const imp =
      typeof profile?.impersonatingTenantId === 'string' ? profile.impersonatingTenantId.trim() : '';
    return imp.length > 0;
  }
  return true;
}

/**
 * Short message when Add patient / patient CSV upload is hidden (wrong role or superuser without impersonation).
 */
export function patientCreateUiHint(profile) {
  const r = String(profile?.role || '').toLowerCase().trim();
  if (r === 'superuser' && !canCreatePatients(profile)) {
    return 'As a superuser, select a tenant to impersonate in the header before adding patients or uploading a CSV.';
  }
  return 'Only administrators, supervisors, and clinical admins can add patients or upload patients via CSV.';
}

/**
 * Filter patient list by scope (Dashboard / Patients table).
 * @param {object[]} patients
 * @param {string} scope - SCOPE_MY | SCOPE_TEAM | SCOPE_ALL
 * @param {{ role?: string, uid?: string, users?: object[] }} ctx
 */
export function filterPatientsByScope(patients, scope, { role, uid, users = [], team = 'all' }) {
  if (!Array.isArray(patients)) return [];
  const teamIds = team === 'rpm' ? getPatientRpmCareManagerIds : getPatientCareManagerIds;
  const r = String(role || '').toLowerCase().trim();
  const viewer = String(uid || '').trim();

  if (r === 'care manager') {
    if (!viewer) return [];
    return patients.filter((p) => teamIds(p).includes(viewer));
  }

  if (r === 'admin' || r === 'superuser' || r === 'clinical admin') {
    if (scope === SCOPE_ALL) return patients;
    if (scope === SCOPE_MY && viewer) {
      return patients.filter((p) => teamIds(p).includes(viewer));
    }
    return patients;
  }

  // Read-only viewer: the Firestore query is already limited to the user's
  // client/provider access filters — no additional scope narrowing here.
  if (r === 'viewer') {
    return patients;
  }

  if (r === 'supervisor') {
    if (!viewer) return [];
    const supervised = getSupervisedCareManagerUidSet(viewer, users);
    if (scope === SCOPE_MY) {
      return patients.filter((p) => teamIds(p).includes(viewer));
    }
    if (scope === SCOPE_TEAM) {
      return patients.filter((p) => {
        const ids = teamIds(p);
        if (ids.length === 0) return false;
        return ids.some((id) => id === viewer || supervised.has(id));
      });
    }
    return patients;
  }

  return patients;
}

/** Effective Firestore tenant for a patient doc (legacy rows without tenantId → trilogy). */
function effectivePatientTenantId(patient) {
  const t = patient?.tenantId;
  if (typeof t === 'string' && t.trim()) return t.trim();
  return 'trilogy';
}

/**
 * Whether the signed-in care app user may view this patient (detail page).
 * Unassigned patients: allowed for admin and global superuser only; impersonating superuser must match patient tenant.
 * @param {{ patient: object, profile: object, viewerUid: string, users?: object[] }} args
 */
export function canCareUserAccessPatient({ patient, profile, viewerUid, users = [] }) {
  const role = String(profile?.role || '').toLowerCase().trim();
  const uid = String(viewerUid || '').trim();
  if (role === 'admin' || role === 'clinical admin') return true;
  if (!patient || !uid) return false;

  // Read-only viewer: denormalized clientId / currentProviderId on the patient
  // doc must match one of the user's access filters (mirrors Firestore rules).
  if (role === 'viewer') {
    const clientIds = Array.isArray(profile?.accessClientIds) ? profile.accessClientIds : [];
    const providerIds = Array.isArray(profile?.accessProviderIds) ? profile.accessProviderIds : [];
    const patientClientId = String(patient.clientId || '').trim();
    const patientProviderId = String(patient.currentProviderId || '').trim();
    return (
      (patientClientId && clientIds.includes(patientClientId))
      || (patientProviderId && providerIds.includes(patientProviderId))
    );
  }

  if (role === 'superuser') {
    const imp =
      typeof profile?.impersonatingTenantId === 'string' && profile.impersonatingTenantId.trim()
        ? profile.impersonatingTenantId.trim()
        : null;
    if (!imp) return true;
    return effectivePatientTenantId(patient) === imp;
  }

  const ids = getPatientCareManagerIds(patient);
  if (ids.length === 0) return false;

  if (role === 'care manager') {
    return ids.includes(uid);
  }

  if (role === 'supervisor') {
    const supervised = getSupervisedCareManagerUidSet(uid, users);
    return ids.some((id) => id === uid || supervised.has(id));
  }

  return false;
}
